import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { getObjectPage } from '../../../utils/hasputils.js';
import { getActionScope, getActionTargetNodes } from '../../../actions/actionPropertyResolver.js';
import { getTemplateVisibilityTargets } from '../../../actions/templateVisibility.js';
import gpioMetadataProvider from '../../../gpio/GpioMetadataProvider.js';

/**
 * Context the action editors need for one source object: property targets on
 * its page, template targets and live GPIO entity metadata.
 *
 * @param {() => object|null} getNode  the source UI object
 * @param {() => object} getSettings   app settings (plate selects GPIO metadata)
 */
export function useActionEditorContext(getNode, getSettings) {
    const targetRevision = ref(0);
    const pageId = computed(() => Number(getActionScope(getNode())?.haspid));
    const targets = computed(() => {
        // Hasp objects are EventTarget-like but their attributes are not Vue refs.
        // Recompute target capabilities when a sibling emits its type adjustment.
        targetRevision.value;
        const node = getNode();
        if (!node) return [];
        return getActionTargetNodes(node).map(item => ({
            value: `${pageId.value}:${Number(item.haspid)}`,
            label: `${item.haspid} — ${item.comment || item.obj.replace(/_/g, ' ').replace(/^./, char => char.toUpperCase())}`,
            node: item,
        }));
    });
    const templateTargets = computed(() => {
        const node = getNode();
        const templatePages = (node?.getLayer?.()?.getChildren?.() ?? []).filter(item => item.obj === 'template_page');
        return node ? getTemplateVisibilityTargets(templatePages, getObjectPage(node)) : [];
    });

    const gpioMetadata = ref({ entities: [], descriptor: null, status: null });
    let stopGpioMetadata = null;
    function syncGpioMetadata() {
        const settings = getSettings();
        gpioMetadataProvider.setSettings(settings);
        stopGpioMetadata?.();
        stopGpioMetadata = gpioMetadataProvider.subscribe(settings?.plate, event => {
            gpioMetadata.value = event;
        });
        const entities = gpioMetadataProvider.listEntities(settings?.plate);
        gpioMetadata.value = {
            entities,
            descriptor: entities.length ? { entities } : null,
            status: gpioMetadataProvider.getStatus(settings?.plate),
        };
    }

    let observedTargets = [];
    const refreshTargets = () => { targetRevision.value++; };
    function removeTargetListener(item) {
        if (item.off) item.off('type', refreshTargets);
        else item.removeEventListener?.('type', refreshTargets);
    }
    function addTargetListener(item) {
        if (item.on) item.on('type', refreshTargets);
        else item.addEventListener?.('type', refreshTargets);
    }
    function attachTargetListeners() {
        observedTargets.forEach(removeTargetListener);
        observedTargets = getActionScope(getNode())?.getChildren?.() ?? [];
        observedTargets.forEach(addTargetListener);
        refreshTargets();
    }

    onMounted(attachTargetListeners);
    watch(getNode, attachTargetListeners);
    // The plate's stored GPIO configuration is an entity source too (see GpioMetadataAdapter).
    watch(() => [getSettings()?.plate?.id ?? getSettings()?.plate?.name, JSON.stringify(getSettings()?.plate?.gpio_config ?? null)],
        syncGpioMetadata, { immediate: true });
    onBeforeUnmount(() => {
        observedTargets.forEach(removeTargetListener);
        stopGpioMetadata?.();
    });

    return { targets, templateTargets, gpioMetadata };
}
